import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import {
  doesValueExist,
  getAskingPrice,
  sentryError,
  getShouldAnswerQuestionsOnEnquire,
  isImage,
} from 'utils';
import {
  INCLUDE_VIDEO_IN_TEASER,
  Listing,
  TEASER_TYPE,
  TEASER_VERIFICATION,
} from 'models/Listing';
import TagIcon from 'icons/Tag.icon';
import SuitcaseIcon from 'icons/Suitcase.icon';
import {
  AskingPricePlaceholder,
  KeyDetailsPlaceholder,
  MainPlaceholder,
  MapPlaceholder,
  TitlePlaceholder,
  VideoPlaceholder,
  WhatsNextPlaceholder,
} from './placeholders';
import { checkIfNotNegative, getIntegerValue, getPricePerField } from './utils';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import ResolvePopup from 'components/ResolvePopup';
import { Map } from './components/Map';
import useMatchMedia from 'hooks/useMatchMedia';
import { BussinessVideo } from './components/BussinessVideo';
import { WhatsNextSection } from './components/WhatsNextSection';
import useGetKeyDetailsOptions from './hooks';
import { PdfViewer } from 'components/PdfViewer';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from 'providers/Auth/Auth.context';
import { ASKING_PRICE_SELECT } from 'models/DealRoom';
import { MainLayoutContext } from 'router/subrouters/Dash/Dash.router';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useOnEnquire } from 'router/subrouters/Dash/subrouters/Business/hooks';
import NudgeInterested from 'components/NudgeInterested';
import api from 'api';
import { BUSINESS_ROUTE } from 'data/global';
import EnquiryContext from 'providers/Enquiry/Enquiry.context';
import mixpanelService from 'services/mixpanelService';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';

import './BusinessTeaser.styles.scss';
import './BusinessTeaser.styles.responsive.scss';

const DISABLE_FOOTER_CLASS = 'business-teaser-footer--disabled';

type BusinessTeaserProps = {
  className?: string;
  isLoading?: boolean;
  listing: Partial<Listing>;
  areActionsDisabled?: boolean;
};

const BusinessTeaser: FC<BusinessTeaserProps> = ({
  className,
  listing,
  isLoading,
  areActionsDisabled,
}) => {
  const classes = classNames('business-teaser', className);
  const isMobile = useMatchMedia('isPhablet');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hash: openEnquiry } = useLocation();
  const { isLoggedIn, previousLocation } = useContext(AuthContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { addFooterClassName, resetFooterClassName } =
    useContext(MainLayoutContext);
  const { onEnquire } = useContext(EnquiryContext);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOnLoggedInEnquire = useOnEnquire(listing);

  const getMixpanelProps = useGetMixpanelProps();

  const needsToBeLoggedIn =
    listing?.teaserVerification ===
      TEASER_VERIFICATION.WITH_EMAIL_VERIFICATION && !isLoggedIn;
  const enableTeaser = listing ? listing.enableTeaser : true;

  const hasTeaserFile =
    listing?.teaserType === TEASER_TYPE.OWN_TEASER_FILE &&
    !!listing?.teaserFile;

  const isFixedPrice =
    listing?.askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE;
  const isEOI =
    listing?.askingPriceSelect === ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST;

  const adjEBITDA = useMemo(() => {
    const { EBITDA, totalAdjustments } = listing || {};
    const intEbitda = getIntegerValue(EBITDA);
    const intAdj = getIntegerValue(totalAdjustments);

    if (!intEbitda || intEbitda < 0) return;
    if (!totalAdjustments) return intEbitda;

    return intEbitda + intAdj;
  }, [listing]);

  const adjEBIT = useMemo(() => {
    const { EBIT, totalAdjustments } = listing || {};
    const intEbit = getIntegerValue(EBIT);
    const intAdj = getIntegerValue(totalAdjustments);

    if (!intEbit || intEbit < 0) return;
    if (!totalAdjustments) return intEbit;

    return intEbit + intAdj;
  }, [listing]);

  const keyDetailsOptions = useGetKeyDetailsOptions(
    listing,
    isFixedPrice,
    isEOI,
    adjEBITDA,
  );

  const keyDetails = useMemo(
    () =>
      isLoading ? (
        <KeyDetailsPlaceholder />
      ) : (
        <div
          className={classNames('white-panel', 'business-teaser__key-details')}
        >
          <p>{t('keyDetails')}</p>
          <div>
            {keyDetailsOptions.map(
              ({ icon, text, popupText, value, popupIcon }) => {
                if (!value) return null;

                return (
                  <span key={text}>
                    {icon}
                    <span className="price">{value}</span>
                    <span>{t(text)}</span>

                    {popupText && (
                      <ResolvePopup
                        onHover
                        contentClassName="business-teaser__popup"
                        popupContent={t(popupText)}
                      >
                        {popupIcon}
                      </ResolvePopup>
                    )}
                  </span>
                );
              },
            )}
          </div>
        </div>
      ),
    [isLoading, keyDetailsOptions, t],
  );

  const askingPrice = useMemo(() => {
    if (isEOI) return null;

    const listingPrice = isFixedPrice ? listing?.amount : listing?.amountFrom;
    const intListingPrice = getIntegerValue(listingPrice);

    const EBITDA = intListingPrice / adjEBITDA;
    const EBIT = intListingPrice / adjEBIT;

    const askingPrice = getAskingPrice({
      ...listing,
      amount: getIntegerValue(listing?.amount),
      amountFrom: getIntegerValue(listing?.amountFrom),
      amountTo: getIntegerValue(listing?.amountTo),
    });

    return isLoading ? (
      <AskingPricePlaceholder />
    ) : (
      <div
        className={classNames('white-panel', 'business-teaser__asking-price')}
      >
        <div>
          <TagIcon /> {t('askingPrice')} ($)
          <span>{askingPrice}</span>
        </div>
        <div>
          <TagIcon />
          <p>
            {t('askingPrice')} / {t('multiple')}
          </p>
          <span>
            <p>{t('adjEBITDALC')}</p>
            {checkIfNotNegative(EBITDA) ? `${EBITDA.toFixed(1)}x` : '-'}
          </span>
          <span>
            <p>{t('adjEBITLC')}</p>
            {checkIfNotNegative(EBIT) ? `${EBIT.toFixed(1)}x` : '-'}
          </span>
        </div>
      </div>
    );
  }, [isEOI, adjEBIT, adjEBITDA, isFixedPrice, isLoading, listing, t]);

  const plStatement = useMemo(() => {
    const {
      revenue,
      COGS,
      grossProfit,
      EBITDA,
      adjustments,
      totalAdjustments,
      lessOverheads,
      depreciationAndAmortisation,
      accountingPeriod,
    } = listing || {};

    const filteredAdjustments = adjustments
      ?.filter(Boolean)
      ?.filter((el) => !!el?.name);

    return isLoading ? (
      <MainPlaceholder />
    ) : (
      <div className={classNames('white-panel', 'business-teaser__statement')}>
        {doesValueExist(accountingPeriod) && (
          <div className="bold title">
            {t('plStatement')} <p>{accountingPeriod}</p>
          </div>
        )}
        {doesValueExist(revenue) && (
          <div className="bold">
            <p>{t('revenue')}</p> <p>{getPricePerField(revenue)}</p>
          </div>
        )}
        {doesValueExist(COGS) && (
          <div>
            <p>{t('COGS')}</p> <p>{getPricePerField(COGS)}</p>
          </div>
        )}
        {doesValueExist(grossProfit) && (
          <div className="bold border">
            <p>{t('grossProfit')}</p>
            <p>{getPricePerField(grossProfit)}</p>
          </div>
        )}
        {doesValueExist(lessOverheads) && (
          <div className="border">
            <p>{t('operatingExpenses')}</p>
            <p>{getPricePerField(lessOverheads)}</p>
          </div>
        )}
        {doesValueExist(depreciationAndAmortisation) && (
          <div className="bold-border">
            <p>{t('depreciationAndAmortisation')}</p>
            <p>{getPricePerField(depreciationAndAmortisation)}</p>
          </div>
        )}
        {checkIfNotNegative(EBITDA) && (
          <div className="bold bold-border">
            <p>{t('EBITDA')}</p>
            <p>{getPricePerField(EBITDA)}</p>
          </div>
        )}
        {!!filteredAdjustments?.length && <div>{t('adjustments')}</div>}
        {!!filteredAdjustments?.length && (
          <ul>
            {filteredAdjustments.map((adjustment) => (
              <li key={adjustment?.name}>
                <p>{adjustment?.name}</p>
                <p>{getPricePerField(adjustment?.netAmount)}</p>
              </li>
            ))}
          </ul>
        )}
        {doesValueExist(totalAdjustments) && (
          <div className="bold-border">
            <p>{t('totalAdjustments')}</p>
            <p>{getPricePerField(totalAdjustments)}</p>
          </div>
        )}
        {checkIfNotNegative(adjEBITDA) && (
          <div className="bold-border">
            <p>{t('adjustedEBITDA')}</p>
            <p>{getPricePerField(adjEBITDA)}</p>
          </div>
        )}

        <span className="bold-border" />
      </div>
    );
  }, [adjEBITDA, isLoading, listing, t]);

  const bussinesVideo = useMemo(
    () =>
      isLoading ? (
        <VideoPlaceholder />
      ) : (
        <BussinessVideo
          hasVideo={
            listing?.includeVideoInTeaser !== INCLUDE_VIDEO_IN_TEASER.NO
          }
          videoSrc={
            listing?.includeVideoInTeaser ===
            INCLUDE_VIDEO_IN_TEASER.WITH_EMBEDDED_CODE
              ? listing?.embeddedVideoCode
              : listing?.embeddedTeaserVideoFile?.url
          }
        />
      ),
    [
      isLoading,
      listing?.embeddedTeaserVideoFile?.url,
      listing?.embeddedVideoCode,
      listing?.includeVideoInTeaser,
    ],
  );

  const registerInterest = useCallback(async () => {
    const shouldAnswerQuestions = getShouldAnswerQuestionsOnEnquire(
      listing?.dealRoom,
    );

    const { firstName, lastName, email, contactNumber } = currentUser || {};

    if (email && isLoggedIn && !shouldAnswerQuestions) {
      setIsSubmitting(true);

      mixpanelService.enquiryCreationInitiated(
        getMixpanelProps({
          dealId: listing?.dealRoomId,
          dealName: listing?.dealRoom?.name,
          enquiryOrigin: ENQUIRY_ORIGIN.ORGANIC,
        }) as EnquiryCreationInitiatedEventProps,
      );

      await handleOnLoggedInEnquire({
        firstName,
        lastName,
        email,
        contactNumber,
      });

      setIsSubmitting(false);
    } else {
      onEnquire(listing?._id);
    }
  }, [
    currentUser,
    isLoggedIn,
    listing,
    getMixpanelProps,
    handleOnLoggedInEnquire,
    onEnquire,
  ]);

  const backLink = useMemo(() => {
    const { pathname, search } = previousLocation || {};

    const to =
      search && !search?.includes('/teaser')
        ? `${BUSINESS_ROUTE}${search}`
        : pathname &&
          pathname?.includes(BUSINESS_ROUTE) &&
          !pathname?.includes('/teaser')
        ? pathname
        : BUSINESS_ROUTE;

    return to;
  }, [previousLocation]);

  const handleNotInterested = useCallback(async () => {
    try {
      setIsSubmitting(true);

      if (isLoggedIn) {
        await api.listings.declineInterest(listing?._id);
      }

      navigate(backLink, { replace: true });
    } catch (error) {
      sentryError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isLoggedIn, backLink, listing, navigate]);

  //if teaser is not available, redirect
  useEffect(() => {
    if (
      (needsToBeLoggedIn && !isLoggedIn) ||
      (!enableTeaser && !areActionsDisabled)
    ) {
      navigate(BUSINESS_ROUTE);
    }
  }, [
    enableTeaser,
    isLoggedIn,
    needsToBeLoggedIn,
    openEnquiry,
    navigate,
    areActionsDisabled,
  ]);

  useEffect(() => {
    if (hasTeaserFile) {
      addFooterClassName?.(DISABLE_FOOTER_CLASS);
    }
    return () => {
      resetFooterClassName?.();
    };
  }, [hasTeaserFile, addFooterClassName, resetFooterClassName]);

  useEffect(() => {
    if (currentUser && listing?._id) {
      api.listings.viewTeaser(listing?._id);
    }
  }, [currentUser, listing?._id]);

  return (
    <div className={classes}>
      {hasTeaserFile ? (
        isImage(listing.teaserFile.type) ? (
          <img src={listing.teaserFile.url} alt={listing.teaserFile.url} />
        ) : (
          <PdfViewer url={listing?.teaserFile?.url} canZoom={false} />
        )
      ) : (
        <div className="business-teaser__content">
          <div className="business-teaser__content__left">
            <div className="business-teaser__tags">
              <span>
                <LocationmarkerIcon />
                {listing?.location?.state || listing?.dealLocation}
              </span>
              <span>
                <SuitcaseIcon /> {listing?.industry}
              </span>
            </div>

            {isLoading ? (
              <TitlePlaceholder />
            ) : (
              <p className="business-teaser__title">{listing.listingTitle}</p>
            )}

            {isMobile && bussinesVideo}

            {keyDetails}
            {plStatement}
            {askingPrice}
          </div>

          <div className="business-teaser__content__right">
            {!isMobile && bussinesVideo}

            {isLoading ? (
              <MapPlaceholder />
            ) : (
              listing.location && <Map location={listing.location} />
            )}

            {isLoading ? (
              <WhatsNextPlaceholder />
            ) : (
              <WhatsNextSection
                hasNda={!!listing?.envelopeTemplateId}
                isDisabled={isSubmitting}
                {...(!areActionsDisabled && {
                  onRegisterClick: registerInterest,
                })}
              />
            )}
          </div>
        </div>
      )}

      {!isLoading && (
        <NudgeInterested
          areActionsDisabled={areActionsDisabled}
          isSubmitting={isSubmitting}
          text={t('businessTeaserNudgeText')}
          notForMe={handleNotInterested}
          registerInterest={registerInterest}
        />
      )}

      {isSubmitting && <Loader />}
    </div>
  );
};

export default BusinessTeaser;
